/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import tokenService from '../services/tokenService';
import userService from '../services/userService';
import cookieService from '../services/cookieService';
import providerService from '../services/providerService';
import authService from '../services/authService';
import userDetails from '../actions/userDetailsAction';
import providerDetails from '../actions/providerDetailsAction';
import auth from '../actions/authAction';
import Redirect from './Redirect';
import {
  ID_TOKEN_COOKIE,
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  DPX_OPTOUT_COOKIE,
  SHARED_MENU_COOKIE,
  USER_COOKIE,
  CACHE_CONTROL_COOKIE,
  routes,
  getAuth0LoginUrl,
  auth0LogoutUrl,
  ACCESS_ROLES,
  buildRegionProbeUrl,
  appEnv,
} from '../config';
import { probeAndPersistRegion } from '../utils/apiRegion';
import Spinner from './Spinner';
import { getBrowserLanguageInfo } from '../utils/languageHelper';

const Authorizer = () => {
  const [loading, setLoading] = useState(true);
  const [nextStep, setNextStep] = useState();
  const [userCountry, setUserCountry] = useState(
    localStorage.getItem('userCountry'),
  );
  const [userLang, setUserLang] = useState(
    localStorage.getItem('userLang'),
  );
  const [professional, setProfessional] = useState();
  const [hasLoginError, setHasLoginError] = useState(false);
  const uDetails = userService.getUserDetails();
  const { role } = uDetails;

  const queryString = useLocation().search;
  const code = new URLSearchParams(queryString).get('code');
  const action = new URLSearchParams(queryString).get('action');
  const errorQS = new URLSearchParams(queryString).get('error');

  // check to see if there are any clinics stored in localStorage
  const providerListLocalStorage =
    providerService.getCachedProviders()?.providerList;

  const dispatch = useDispatch();

  const tokenExists = () =>
    !!cookieService.getCookie(ACCESS_TOKEN_COOKIE);

  useEffect(() => {
    let isMounted = true;

    const proceed = async () => {
      if (code) {
        setLoading(true);
        try {
          const result =
            await tokenService.convertAuthorisationCodeIntoTokens(
              code,
            );

          if (!isMounted) return;

          const { idToken, accessToken, refreshToken, expiresIn } =
            result;

          const expiresInMins = expiresIn ? expiresIn / 60 : 30;
          cookieService.setCookie(
            ID_TOKEN_COOKIE,
            idToken,
            expiresInMins * 4,
          );
          // save the professional Token in a cookie
          cookieService.setCookie(
            ACCESS_TOKEN_COOKIE,
            accessToken || '',
            expiresInMins,
          );
          // save the professional Refresh Token in a cookie
          cookieService.setCookie(
            REFRESH_TOKEN_COOKIE,
            refreshToken || '',
            expiresInMins,
          );

          const user = userService.decodeIdToken(idToken);
          setProfessional(user);
        } catch (error) {
          if (isMounted) setHasLoginError(true);
        }
      } else if (action === 'logout') {
        setLoading(true);

        authService.logout().then(() => {
          window.location.replace(auth0LogoutUrl);
          setLoading(false);
        });
      } else if (errorQS === 'access_denied') {
        // logout if auth0 returns with error=access_denied
        authService.logout().then(() => {
          window.location.replace(auth0LogoutUrl);
          setLoading(false);
        });
      } else if (tokenExists()) {
        // user is already logged in - they must have accessed via new tab
        // take them to home or resources based on role
        setLoading(false);
        setNextStep(
          role === ACCESS_ROLES.RESOURCES_AND_TRAINING
            ? routes.dpx.resources
            : routes.dpx.home,
        );
      } else {
        // Before sending the user to Auth0, probe eu/gl reachability
        // so the working region is persisted to localStorage.  This
        // avoids a login-loop where the token call lands on the wrong
        // domain after returning from Auth0.
        // Skip probe on OFFLINE (localhost has no dual-region setup).
        if (appEnv !== 'OFFLINE') {
          try {
            await probeAndPersistRegion(buildRegionProbeUrl);
          } catch {
            // Probe failure must never block the Auth0 redirect.
          }
        }
        window.location.replace(getAuth0LoginUrl());
      }
    };

    proceed();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const handleProfessional = async () => {
      const country = professional.countryCode?.toLowerCase();

      const browserLanguage = getBrowserLanguageInfo();
      localStorage.setItem(
        'browserLang',
        browserLanguage.fullLanguage,
      );

      const lang = professional.languageCode?.slice(0, 2);
      setUserCountry(country);
      localStorage.setItem('userCountry', country);
      setUserLang(lang);
      localStorage.setItem('userLang', lang);

      cookieService.setCookie(
        USER_COOKIE,
        JSON.stringify({ country, lang }),
        Infinity,
        true,
      );

      // store the user info in state
      dispatch(userDetails(professional));
      // set auth state
      dispatch(auth(true));

      if (
        providerListLocalStorage &&
        providerListLocalStorage.professionalId ===
          professional.professionalId
      ) {
        if (!isMounted) return;
        // clinics are in localStorage

        setLoading(false);

        if (providerListLocalStorage.providers.length <= 1) {
          setNextStep(routes.dpx.initializer);
        } else {
          setNextStep(routes.dpx.selectClinics);
        }
      } else {
        let fetchFromCache = true;
        const cacheControlCookie = cookieService.getCookie(
          CACHE_CONTROL_COOKIE,
        );
        const cacheControl = cacheControlCookie
          ? JSON.parse(cacheControlCookie)
          : null;
        if (cacheControl) {
          if (
            cacheControl.professionalId ===
            professional.professionalId
          ) {
            fetchFromCache = false;
            cookieService.removeCookie(CACHE_CONTROL_COOKIE);
          }
        }

        // get clinics from backend
        const {
          next,
          providerList,
          providerId,
          hasClinic,
          countryCode,
        } = await providerService.checkClinics(
          professional.professionalId,
          professional.countryCode,
          fetchFromCache, // use API cache or not
        );

        if (providerList && hasClinic) {
          const providerDetailsData =
            providerService.storeProviderDetails(
              providerId,
              providerList,
              countryCode,
            );

          // save provider details in state
          dispatch(providerDetails(providerDetailsData));
        }

        if (providerList && !hasClinic) {
          // remove provider details from localStorage
          providerService.removeProviderDetails();
        }

        if (next.startsWith(routes.mcp.domain)) {
          // redirect back to mcp
          cookieService.setCookie(
            DPX_OPTOUT_COOKIE,
            true,
            Infinity,
            true,
          );
          cookieService.setCookie(SHARED_MENU_COOKIE, false, 0, true);
        }

        if (!isMounted) return;

        // move on to next step
        setLoading(false);
        setNextStep(next);
      }
    };

    if (professional) {
      handleProfessional();
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [professional]);

  return hasLoginError ? (
    <Redirect to={routes.dpx.unauthorized} />
  ) : loading ? (
    <Spinner loading={loading} />
  ) : nextStep ? (
    nextStep.includes(routes.dpx.initializer) ? (
      <Redirect push to={nextStep} />
    ) : (
      <Redirect to={nextStep} country={userCountry} lang={userLang} />
    )
  ) : (
    <></>
  );
};

export default Authorizer;
